import React, { useMemo, useRef, useState } from "react";
import type { Header, SortingState, Table } from "@tanstack/react-table";
import ColumnChoosingPopover from "./ColumnChoosingPopover";
import "./TableActions.scss";
import { IconButton, RadioButtonsList } from "../../../controls";
import { useTranslation } from "react-i18next";
import SortPopover from "./SortPopover";
import { sortModeEnum } from "../Table";

// TODO: sortModeEnum is currently typed as `any` in Table.d.ts; mirror the
// runtime union here until Table.js is converted.
type SortMode = "NONE" | "EDIT" | "READ_ONLY";

interface TableActionsProps<TData = any> {
  grouping?: string[];
  setGrouping: (grouping: string[]) => void;
  table: Table<TData>;
  getActionComponent?: (table: Table<TData>) => React.ReactNode;
  enableColumnChoosing?: boolean;
  sortMode?: SortMode;
  sortHelp?: React.ReactNode;
  onExpandTable?: () => void;
  resetColumns?: () => void;
  defaultSortBy?: SortingState;
  onSortChange?: (sort: SortingState) => void;
  tableLoading?: boolean;
  forceGrouping?: boolean;
  extraToolbarContent?: React.ReactNode;
}

const TableActions = <TData,>({
  grouping,
  setGrouping,
  table,
  getActionComponent,
  enableColumnChoosing,
  sortMode,
  sortHelp,
  onExpandTable,
  resetColumns,
  defaultSortBy,
  onSortChange,
  tableLoading,
  forceGrouping,
  extraToolbarContent,
}: TableActionsProps<TData>) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const { t } = useTranslation();

  const ActionComponent = useMemo(() => {
    return getActionComponent?.(table);
  }, [getActionComponent, table?.getSelectedRowModel()?.rows]);

  const groupingEnabledColumns = useRef(
    table?.getHeaderGroups()?.reduce<Header<TData, unknown>[]>((acc, curr) => {
      return [
        ...acc,
        ...curr.headers.filter(
          // groupingLabel is a custom columnDef meta property not present on the base ColumnDef type
          (header) =>
            (header?.column?.columnDef?.meta as { groupingLabel?: string })
              ?.groupingLabel
        ),
      ];
    }, [])
  );

  return (
    <div
      className={`table-actions ${!!ActionComponent ? "has-actions" : ""} ${
        enableColumnChoosing ? "has-column-choosing" : ""
      } ${!!onExpandTable ? "has-expand-table" : ""}`}
    >
      {(!!groupingEnabledColumns?.current?.length ||
        sortMode !== sortModeEnum.NONE) && (
        <div className="table-group-by-sort-container">
          {!!groupingEnabledColumns?.current?.length && (
            <RadioButtonsList
              label={t("Group by")}
              tabStyle
              noMargin
              hideLabelHelper
              options={[
                ...(forceGrouping
                  ? []
                  : [
                      {
                        name: "None",
                        label: t("None"),
                        value: "None",
                      },
                    ]),
                ...groupingEnabledColumns?.current?.map((originalColumn) => {
                  return {
                    label: t(
                      (
                        originalColumn.column.columnDef.meta as {
                          groupingLabel?: string;
                        }
                      ).groupingLabel!
                    ),
                    value: originalColumn.column.id,
                    name: originalColumn.column.id,
                  };
                }),
              ]}
              onChange={(val) => {
                if (val === "None") {
                  setGrouping([]);
                } else {
                  setGrouping([val]);
                }
              }}
              value={grouping?.length ? grouping[0] : "None"}
            />
          )}
          {sortMode !== sortModeEnum.NONE && (
            <SortPopover
              table={table}
              isReadOnly={sortMode === sortModeEnum.READ_ONLY}
              tableLoading={tableLoading}
              defaultSortBy={defaultSortBy}
              onSortChange={onSortChange!}
              sortHelp={sortHelp}
            />
          )}
        </div>
      )}
      {!!extraToolbarContent && (
        <span className="table-actions-extra-left">{extraToolbarContent}</span>
      )}
      {!!ActionComponent && (
        <div className={"actions-wrapper"}> {ActionComponent}</div>
      )}
      {enableColumnChoosing && (
        <ColumnChoosingPopover
          table={table as unknown as Table<unknown>}
          resetColumns={resetColumns!}
        />
      )}
      {!!onExpandTable && (
        <IconButton
          isIcon={"seem-icon-chevron-down"}
          className={isFullScreen ? "" : "rotate-chevron"}
          withTooltip={{
            content: isFullScreen ? t("Collapse") : t("Expand"),
          }}
          data-testid={"expand-table-button"}
          onClick={() => {
            onExpandTable?.();
            setIsFullScreen((prevState) => !prevState);
          }}
        />
      )}
    </div>
  );
};

export default TableActions;
